import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { userSessionState } from '@aschehoug/aschehoug-oidc-client/dist/AMTypes'
import { useRouter } from 'vue-router'
import type { SessionUserInfo, User, SessionUserAttributes } from '@@/types/User'
import { useKlossToast } from '@/composables/useKlossToast'
import { aschehougOidcClient } from '@/aschehougOidcClient'
import Language from '~~/enums/Language'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const klossToast = useKlossToast()
  const { initPendo } = usePendo()
  const nuxtApp = useNuxtApp()

  const data = ref<User | null>(null)
  const isLoading = ref<boolean>(true)
  const isAuthenticated = computed(() => data.value !== null)
  const isTeacher = computed(() => data.value?.role.includes('teacher') || false)
  const isStudent = computed(() => data.value?.role.includes('student') || false)

  const { fetchCoreExternal } = useVgsFetch(data?.value?.preferredLanguage ?? Language.NB)
  const fetchSessionUser = async (): Promise<SessionUserInfo> => await fetchCoreExternal('/frontend/session/user')
  const fetchSessionAttributes = async (): Promise<SessionUserAttributes> => await fetchCoreExternal('/frontend/session/attributes')

  const init = async (): Promise<void> => {
    try {
      const userData = await fetchSessionUser()
      const sessionAttributes = await fetchSessionAttributes()
      data.value = { ...userData, sessionAttributes }
      initPendo(data.value)
      nuxtApp.$i18n.setLocale(data.value.preferredLanguage || Language.NB)
    }
    catch {
      throw new Error('Couldn\'t get user')
    }
  }

  const isInAschehougOrganization = computed(() => data.value?.schoolOrganizations.some(org => org.name.toLowerCase().includes('aschehoug')))

  const logout = async () => {
    localStorage.clear()
    await aschehougOidcClient.logout()
  }

  const handleRedirectAfterLogin = async () => {
    // Clean up the URL by removing oidc query parameters
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('iss')
    url.searchParams.delete('state')
    url.searchParams.delete('client_id')
    router.replace(`${url.pathname}?${url.searchParams.toString()}`)

    const redirectRoute = sessionStorage.getItem('modus-redirect-route')

    if (!redirectRoute) return

    router.replace(redirectRoute)
    sessionStorage.removeItem('modus-redirect-route')
  }

  const handleLogin = async () => {
    try {
      isLoading.value = true
      const sessionState = await aschehougOidcClient.startAuthSession()

      if (sessionState === userSessionState.noActiveSessionFound) return data.value = null

      await init()

      handleRedirectAfterLogin()
    }
    catch (error) {
      console.error(error)
      klossToast.error('Logg-inn feilet.')
      await logout()
    }
    finally {
      isLoading.value = false
    }
  }

  watch(router.currentRoute, async () => {
    // try to log in on route change if not already logged in
    if (aschehougOidcClient.getAccessToken() || isLoading.value) return

    await handleLogin()
  })

  return {
    user: data,
    isTeacher,
    isStudent,
    isInAschehougOrganization,
    isAuthenticated,
    isLoading,
    logout,
    handleLogin,
  }
}, { persist: true })
